import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/common/Breadcrumb";
import ArticleCard from "@/components/common/ArticleCard";
import NewsFilter from "@/pages/news/components/NewsFilter";
import NewsSidebar from "@/pages/news/components/NewsSidebar";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { SITE_ORIGIN } from "@/config/site";

export default function NewsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("cat") ?? "all";
  const keyword = searchParams.get("q") ?? "";

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return articles.filter((article) => {
      const matchCategory = activeCategory === "all" || article.category === activeCategory;
      const matchKeyword =
        kw === "" ||
        article.title.includes(kw) ||
        article.excerpt.includes(kw) ||
        article.categoryName.includes(kw) ||
        article.tags.some((tag) => tag.includes(kw));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const currentCategory = categories.find((item) => item.slug === activeCategory);

  const selectCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug === "all") {
      next.delete("cat");
    } else {
      next.set("cat", slug);
    }
    setSearchParams(next);
  };

  const submitKeyword = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("q", value);
    } else {
      next.delete("q");
    }
    setSearchParams(next);
  };

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 娱乐瓜料精选 | 诱惑吃瓜`
    : "全部瓜料 - 娱乐热点与热帖精选 | 诱惑吃瓜";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: currentCategory ? `${currentCategory.name}栏目` : "全部瓜料",
      description: currentCategory
        ? `${currentCategory.name}：${currentCategory.desc}。`
        : "诱惑吃瓜全部内容汇总，覆盖娱乐热点、剧集讨论、综艺现场与网络热议话题。",
      url: `${SITE_ORIGIN}/news`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "全部瓜料", item: `${SITE_ORIGIN}/news` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: filtered.map((article, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: article.title,
        url: `${SITE_ORIGIN}/article/${article.id}`,
      })),
    },
  ];

  return (
    <>
      <Seo
        title={pageTitle}
        description="浏览诱惑吃瓜全部瓜料：娱乐热点、剧集复盘、综艺名场面、巡演现场与网络热议话题，支持按栏目筛选与关键词搜索，每日更新。"
        keywords="吃瓜列表,娱乐瓜料,剧集热评,综艺高能,网络热议,诱惑吃瓜"
        path="/news"
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100 px-4 py-8 md:px-6 md:py-10">
        <div className="max-w-3xl">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "全部瓜料" }]} />
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            {currentCategory ? currentCategory.name : "全部瓜料"}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            {currentCategory
              ? currentCategory.desc
              : "这里汇集了站内所有整理稿，按照更新时间与热度排列。你可以先用栏目筛选缩小范围，再挑感兴趣的慢慢看。"}
          </p>
        </div>
      </div>

      <div className="px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          <div className="lg:col-span-3">
            <NewsFilter
              activeCategory={activeCategory}
              keyword={keyword}
              total={filtered.length}
              onSelectCategory={selectCategory}
              onSubmitKeyword={submitKeyword}
            />

            {filtered.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {filtered.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                  暂时没有相关的瓜料
                </h2>
                <p className="mt-2 max-w-sm text-sm text-foreground-600">
                  换个关键词试试，或者直接切换到「全部」栏目，也许有新的发现。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    selectCategory("all");
                    submitKeyword("");
                  }}
                  className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <NewsSidebar />
          </div>
        </div>
      </div>
    </>
  );
}